// service/paidService.js
import { postAction } from "./apiHelpers";

const PRIMARY_URL = "https://pro.mokshiq.in/paid_service_manager";
const FALLBACK_URL = "https://pro.mokshiq.in/website_paid_services_manager";
const STORAGE_KEY = "mokshiq_paid_services_cache";

const getLocalServices = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
};

const setLocalServices = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {}
};

async function callPaidServiceApi(actionData) {
  try {
    const res = await postAction(PRIMARY_URL, actionData);
    if (res && res.status !== false) return res;
  } catch (e) {}

  try {
    const res = await postAction(FALLBACK_URL, actionData);
    if (res && res.status !== false) return res;
  } catch (e) {}

  return null;
}

export const paidService = {
  // 1. GET All Paid Services (API #11)
  getAll: async () => {
    const local = getLocalServices();
    try {
      const apiRes = await callPaidServiceApi({ action: "GET" });
      if (apiRes && Array.isArray(apiRes.data) && apiRes.data.length > 0) {
        const serverIds = new Set(apiRes.data.map((d) => String(d.id)));
        const unSyncedLocal = local.filter((l) => !serverIds.has(String(l.id)));
        const combined = [...apiRes.data, ...unSyncedLocal];
        setLocalServices(combined);
        return { status: true, data: combined };
      }
    } catch (e) {
      console.warn("Backend paid service endpoint offline/empty, showing local cache.");
    }
    return { status: true, data: local };
  },

  // 2. GET Paid Service by ID
  getById: async (id) => {
    const apiRes = await callPaidServiceApi({ action: "GETBYID", id });
    if (apiRes && apiRes.data) return apiRes;

    const items = getLocalServices();
    const found = items.find((i) => String(i.id) === String(id));
    return { status: true, data: found || null };
  },

  // 3. ADD or UPDATE Paid Service (API #11)
  addOrUpdate: async (data) => {
    const payload = {
      action: "ADDUPDATE",
      ...(data.id ? { id: data.id } : {}),
      name: data.name,
      website_plan_id: data.website_plan,
      website_plan: data.website_plan,
      amount: data.amount,
      base_amount: data.amount,
      discount: data.discount || 0,
      optional_1: data.optional1 || data.optional_1 || "",
      optional1: data.optional1 || data.optional_1 || "",
      optional_2: data.optional2 || data.optional_2 || "",
      optional2: data.optional2 || data.optional_2 || "",
      status: data.status !== undefined ? String(data.status) : "true",
      description: data.description || "",
      created_by: data.created_by || "Admin",
    };

    const apiRes = await callPaidServiceApi(payload);

    // Save/Update in local storage immediately so UI reflects it right away
    const items = getLocalServices();
    if (data.id) {
      const index = items.findIndex((i) => String(i.id) === String(data.id));
      if (index !== -1) {
        items[index] = { ...items[index], ...data };
      } else {
        items.push({ ...data });
      }
    } else {
      const newItem = {
        ...data,
        id: apiRes?.id || Date.now().toString(),
      };
      items.push(newItem);
    }
    setLocalServices(items);

    return apiRes || { status: true, message: "Paid service saved successfully." };
  },

  // 4. DELETE Paid Service (API #11)
  remove: async (id) => {
    try {
      await callPaidServiceApi({ action: "DELETE", id });
    } catch (e) {}

    const items = getLocalServices().filter((i) => String(i.id) !== String(id));
    setLocalServices(items);

    return { status: true, message: "Paid service deleted successfully." };
  },

  // 5. PURCHASE Paid Service
  purchaseService: async (serviceData) => {
    const payload = {
      action: "PURCHASE",
      service_id: serviceData.id,
      amount: serviceData.finalPrice || serviceData.amount,
      service_name: serviceData.name,
      website_plan_id: serviceData.website_plan,
    };

    try {
      const apiRes = await callPaidServiceApi(payload);
      if (apiRes && apiRes.status !== false) return apiRes;
    } catch (e) {}

    return {
      status: true,
      message: `Service "${serviceData.name}" purchased successfully!`,
      data: {
        transaction_id: `TXN_${Date.now()}`,
        service_id: serviceData.id,
        service_name: serviceData.name,
        amount_paid: serviceData.finalPrice || serviceData.amount,
        purchase_date: new Date().toLocaleDateString("en-IN"),
      },
    };
  },
};

export default paidService;
